import type {
  IAssetCategory,
  IAssetClassification,
  IAssetRatingSeverity,
  IAssetScoreField,
} from '@/lib/constants/assetManager'
import { ASSET_CATEGORIES, ASSET_CLASSIFICATIONS, ASSET_RATING_SEVERITIES } from '@/lib/constants/assetManager'

export const ASSET_MANAGER = {
  TABLE: {
    TITLE: 'Inventario de Activos',
    SEARCH_PLACEHOLDER: 'Buscar por ID, activo o responsable',
    NO_RESPONSIBLE: 'Sin asignar',
  },
  ACTIONS: {
    BULK_UPLOAD: 'Carga masiva',
    NEW_ASSET: 'Nuevo activo',
    APPLY: 'Aplicar',
  },
  TABLE_HEADER: {
    ID: 'ID',
    ASSET: 'Activo',
    CATEGORY: 'Categoría',
    RESPONSIBLE: 'Responsable',
    LOCATION: 'Ubicación',
    RATING: 'Valoración',
    CLASSIFICATION: 'Clasificación',
  },
  FILTERS: {
    ALL: 'Todos',
    FILTER_BY_CATEGORY: 'Filtrar por categoría',
    FILTER_BY_RESPONSIBLE: 'Filtrar por responsable',
    FILTER_BY_CLASSIFICATION: 'Filtrar por clasificación',
  },
  CREATION: {
    TITLE: 'Nuevo activo',
    NAME: 'Nombre',
    CREATE: 'Crear activo',
    CANCEL: 'Cancelar',
    ERROR_MESSAGE: 'Completa todos los campos antes de crear el activo',
    SUCCESS_MESSAGE: (assetCode: string) => `Activo ${assetCode} creado`,
  },
  BULK_UPLOAD: {
    TITLE: 'Importar activos desde CSV',
    DESCRIPTION:
      'Sube un archivo CSV para importar activos de forma masiva. Todas las filas deben ser válidas: si alguna tiene errores, no se crea nada y los errores se muestran a continuación.',
    UPLOAD_FILE: 'Subir CSV',
    UPLOADING: 'Subiendo...',
    DOWNLOAD_SAMPLE: 'Descargar CSV de ejemplo',
    CANCEL: 'Cancelar',
    SUCCESS: 'Activos importados correctamente',
    RELOAD_ERROR: 'Los activos se importaron, pero no se pudo actualizar la lista. Recarga la página para verlos.',
    VALIDATION_TABLE_ROW: 'Fila',
    VALIDATION_TABLE_FIELD: 'Campo',
    VALIDATION_TABLE_ERROR: 'Error',
    VALIDATION_TABLE_RECEIVED_VALUE: 'Valor recibido',
    HEADER_ERROR_TITLE: 'La cabecera de tu CSV tiene problemas',
    HEADER_ERROR_DESCRIPTION:
      'Corrige la fila de cabecera en tu archivo y vuelve a subirlo. Las columnas aceptadas se muestran a continuación.',
    ACCEPTED_COLUMNS_REQUIRED: 'Columnas obligatorias',
    ACCEPTED_COLUMNS_OPTIONAL: 'Columnas opcionales',
    SUMMARY_LINE:
      '{totalRows} filas revisadas, {errorCount} errores encontrados. Corrígelos en tu CSV y vuelve a subirlo.',
    ERROR_UNKNOWN_COLUMN: 'Columna desconocida: {field}',
    ERROR_MISSING_REQUIRED_COLUMN: 'Falta la columna obligatoria: {field}',
    ERROR_EMPTY_FILE: 'El archivo tiene cabecera pero ninguna fila de activos',
    ERROR_MALFORMED_ROW: 'El número de valores de esta fila no coincide con la cabecera',
    ERROR_REQUIRED: '{field} es obligatorio',
    ERROR_TOO_LONG: '{field} no puede superar los 255 caracteres',
    ERROR_INVALID_SCORE: '{field} debe ser un número entero del 1 al 5. Recibido: "{receivedValue}"',
    ERROR_INVALID_OPTIONAL_SCORE:
      '{field} debe ser un número entero del 1 al 5, o vacío si no aplica. Recibido: "{receivedValue}"',
    ERROR_INVALID_CATEGORY: 'Categoría no válida "{receivedValue}". Valores permitidos: {allowedValues}',
    ERROR_INVALID_CLASSIFICATION: 'Clasificación no válida "{receivedValue}". Valores permitidos: {allowedValues}',
    ERROR_DEPARTMENT_NOT_FOUND:
      'Departamento no encontrado en esta organización: "{receivedValue}". Departamentos disponibles: {allowedValues}',
    ERROR_DUPLICATE_IN_FILE: 'Nombre de activo duplicado en este archivo: "{receivedValue}"',
    ERROR_DUPLICATE_IN_DB: 'Ya existe un activo con este nombre: "{receivedValue}"',
  },
  DRAWER: {
    DESCRIPTION: 'Descripción',
    ATTRIBUTES: 'Atributos',
    CATEGORIZATION: 'Categorización del activo',
    FINAL_RATING: 'Valoración final',
    FINAL_RATING_HINT: 'Media de las dimensiones aplicables (C, I, D, A, T)',
    NOTES: 'Notas',
    NOT_APPLICABLE: 'No aplica',
    QUANTITY_SCOPE: 'Cantidad / Alcance',
    NO_DESCRIPTION: 'Sin descripción',
    NO_NOTES: 'Sin notas',
    UPDATE_ERROR: 'No se pudo actualizar el activo',
    DELETE_SUCCESS: 'Activo eliminado correctamente',
    DELETE_ERROR: 'No se pudo eliminar el activo',
    SELECT_CATEGORY: 'Selecciona una categoría',
    SELECT_RESPONSIBLE: 'Selecciona un departamento',
    SEARCH_CATEGORIES: 'Buscar categorías...',
    SEARCH_RESPONSIBLES: 'Buscar departamentos...',
    NO_CATEGORY_FOUND: 'No se encontraron categorías.',
    NO_RESPONSIBLE_FOUND: 'No se encontraron departamentos.',
    DELETE_DIALOG: {
      TITLE: 'Eliminar Activo',
      QUESTION: '¿Estás seguro de querer eliminar el activo ',
      CANCEL: 'Cancelar',
      DELETE: 'Eliminar',
    },
  },
  SCORES: {
    confidentialityScore: 'Confidencialidad',
    integrityScore: 'Integridad',
    availabilityScore: 'Disponibilidad',
    authenticityScore: 'Autenticidad',
    traceabilityScore: 'Trazabilidad',
  } as Record<IAssetScoreField, string>,
  CATEGORIES: {
    [ASSET_CATEGORIES.BUSINESS_PROCESS]: 'Procesos y Actividades de Negocio',
    [ASSET_CATEGORIES.INFORMATION]: 'Información',
    [ASSET_CATEGORIES.HARDWARE]: 'Hardware',
    [ASSET_CATEGORIES.SOFTWARE]: 'Software',
    [ASSET_CATEGORIES.NETWORK_COMMUNICATIONS]: 'Red y Comunicaciones',
    [ASSET_CATEGORIES.PERSONNEL]: 'Personal',
    [ASSET_CATEGORIES.PHYSICAL_FACILITIES]: 'Instalaciones Físicas',
    [ASSET_CATEGORIES.SUPPLIERS]: 'Proveedores',
  } as Record<IAssetCategory, string>,
  CLASSIFICATIONS: {
    [ASSET_CLASSIFICATIONS.INTERNAL]: 'Interna',
    [ASSET_CLASSIFICATIONS.PUBLIC]: 'Pública',
    [ASSET_CLASSIFICATIONS.CONFIDENTIAL]: 'Confidencial',
  } as Record<IAssetClassification, string>,
  RATING_SEVERITIES: {
    [ASSET_RATING_SEVERITIES.CRITICAL]: 'Crítica',
    [ASSET_RATING_SEVERITIES.HIGH]: 'Alta',
    [ASSET_RATING_SEVERITIES.MEDIUM]: 'Media',
    [ASSET_RATING_SEVERITIES.LOW]: 'Baja',
    [ASSET_RATING_SEVERITIES.VERY_LOW]: 'Muy baja',
  } as Record<IAssetRatingSeverity, string>,
}
